import {
    useGetYandexCounters,
    useGetYandexGoals,
    useGetYandexProfileQuery,
    useMutationSetYandexMetrics,
    useMutationYandexAuthUrlQuery,
    useMutationYandexLogout,
} from '@/shared/api'
import { YandexIdLogoSVGIcon, YandexLogoSVGIcon } from '@/shared/assets'
import {
    CheckIcon,
    ContentContainer,
    SelectDropdown,
    ShadcnButton,
} from '@/shared/ui'
import { cn } from '@/true-fsd/shared/utils/css'
import { useEffect, useState } from 'react'
import clsx from 'clsx'

import classes from './SiteIntegration.module.scss'
import { useActiveWorkspace } from '@/true-fsd/data/advertiser-account/hooks'

const notLoggedInText =
    'Для автоматического проброса метрик: Users, Clicks, CPC, CPV, Lead, CR%, CPL требуется авторизация под тем логином Я.Метрики у которого есть доступ к счетчику для вашего сайта. '
const loggedInText =
    'Для сбора аналитики требуется авторизоваться под тем логином Я.Метрики у которого есть доступ к счетчику для вашего сайта. '

const YandexIntegration = ({
    isAdmin,
    className,
    siteId,
}: {
    isAdmin: boolean
    siteId: number
    className?: string
}) => {
    const { mutateAsync: getYandexAuthUrl } =
        useMutationYandexAuthUrlQuery(siteId)

    const handleLogin = async () => {
        const result = await getYandexAuthUrl()
        window.location.href = result.authUrl
    }

    return (
        <ContentContainer
            className={clsx(
                classes['yandex-integration-container'],
                'flex w-full max-w-[300px] flex-col items-center justify-center gap-6 p-5',
                className
            )}
        >
            <YandexIdLogoSVGIcon />
            <ShadcnButton
                disabled={!isAdmin}
                className="flex h-14 w-64 items-center gap-3 rounded-xl bg-black text-base text-white hover:bg-black/80"
                variant="ghost"
                onClick={handleLogin}
            >
                <YandexLogoSVGIcon className="min-h-6 min-w-6" />

                <span className="text-white">Войти с Яндекс ID</span>
            </ShadcnButton>
        </ContentContainer>
    )
}

type SiteGridRowProps = {
    header: string
    children: React.ReactNode
    checked: boolean
}
const SiteGridRow = ({ header, children, checked }: SiteGridRowProps) => {
    return (
        <>
            <h3 className="text-2xl font-bold text-secondary">{header}</h3>
            <div>{children}</div>
            <CheckIcon checked={checked} />
        </>
    )
}

export const SiteIntegration = ({ siteId }: { siteId: number }) => {
    const [selectedCounter, setSelectedCounter] = useState({
        value: 0,
        label: '',
    })

    const [selectedGoal, setSelectedGoal] = useState({
        value: 0,
        label: '',
    })

    const { permission } = useActiveWorkspace()
    const isAdmin = permission.admin

    const { mutate: logout } = useMutationYandexLogout(siteId)
    const { mutate: setYandexMetrics } = useMutationSetYandexMetrics()

    const { data: yandexProfile } = useGetYandexProfileQuery(siteId)

    const { data: yandexCounters } = useGetYandexCounters(siteId, {
        enabled: !!yandexProfile,
    })

    const preparedYandexCounters = yandexCounters?.map((counter) => ({
        value: counter.counter_id.toString(),
        label: counter.counter_name,
    }))

    const { data: yandexGoals } = useGetYandexGoals(
        siteId,
        selectedCounter.value ?? 0,
        {
            enabled: !!selectedCounter,
        }
    )

    const preparedYandexGoals = yandexGoals?.map((goal) => ({
        value: goal.goal_id.toString(),
        label: goal.goal_name,
    }))

    const handleLogout = () => {
        logout()
    }

    const handleSelectGoal = (option: { value: string; label: string }) => {
        setSelectedGoal({
            label: option.label,
            value: +option.value,
        })
        setYandexMetrics({
            siteId,
            body: {
                counterId: selectedCounter.value,
                goalId: +option.value,
                counterName: selectedCounter.label,
                goalName: option.label,
            },
        })
    }

    useEffect(() => {
        setSelectedCounter({
            label: yandexProfile?.counterName ?? '',
            value: yandexProfile?.counterId ?? 0,
        })
        setSelectedGoal({
            label: yandexProfile?.goalName ?? '',
            value: yandexProfile?.goalId ?? 0,
        })
    }, [yandexProfile, setSelectedCounter])

    return (
        <div className={classes['site-integration']}>
            <ContentContainer className="flex w-full max-w-2xl flex-col gap-6">
                <div className="flex justify-between gap-8">
                    <p className="flex-1 text-xs">
                        {yandexProfile ? loggedInText : notLoggedInText}
                    </p>
                    {yandexProfile && (
                        <div className="flex gap-3">
                            <div className="flex flex-col items-end">
                                <span className="min-w-fit text-xs font-semibold">
                                    {yandexProfile?.realName}
                                </span>
                                <button
                                    onClick={handleLogout}
                                    disabled={!isAdmin}
                                    className={cn(
                                        'text-[10px] text-blue-primary',
                                        !isAdmin && 'cursor-not-allowed'
                                    )}
                                >
                                    Выйти
                                </button>
                            </div>
                            <img
                                src={yandexProfile?.avatarUrl}
                                alt="Yandex Logo"
                                className="h-7 w-7 rounded-full"
                            />
                        </div>
                    )}
                </div>
                <div className={classes.fields}>
                    <SiteGridRow header={'1'} checked={!!yandexProfile}>
                        <div className="flex h-[30px] items-center rounded-md border border-stroke p-2 px-4 text-[12px]">
                            {yandexProfile
                                ? yandexProfile.realName
                                : 'Авторизация'}
                        </div>
                    </SiteGridRow>

                    {!yandexProfile && (
                        <>
                            <YandexIntegration
                                siteId={siteId}
                                isAdmin={isAdmin}
                                className="col-span-2"
                            />
                            <div />
                        </>
                    )}

                    <SiteGridRow
                        header={'2'}
                        checked={!!selectedCounter.value && !!yandexProfile}
                    >
                        <SelectDropdown
                            disabled={!yandexProfile || !isAdmin}
                            noArrow={!yandexProfile}
                            placeholder="Выберите счетчик"
                            options={preparedYandexCounters ?? []}
                            value={selectedCounter.value.toString() ?? ''}
                            onSelect={(option) => {
                                setSelectedCounter({
                                    label: option.label,
                                    value: +option.value,
                                })
                            }}
                        ></SelectDropdown>
                    </SiteGridRow>

                    <SiteGridRow
                        header={'3'}
                        checked={
                            !!selectedGoal.value &&
                            !!yandexProfile &&
                            !!preparedYandexGoals?.length
                        }
                    >
                        <SelectDropdown
                            disabled={!yandexProfile || !isAdmin}
                            noArrow={!yandexProfile}
                            placeholder="Выберите цель"
                            options={preparedYandexGoals ?? []}
                            value={selectedGoal.value.toString() ?? ''}
                            onSelect={(option) => handleSelectGoal(option)}
                        ></SelectDropdown>
                    </SiteGridRow>
                </div>
            </ContentContainer>
        </div>
    )
}
